const PDFJS_URL = new URL('./ziloop-pdf.min.js', import.meta.url).href;
const PDF_WORKER_URL = new URL('./ziloop-pdf.worker.min.js', import.meta.url).href;
let pdfjsPromise;
const pdfDocumentCache = new Map();

function loadPdfJs() {
  if (!pdfjsPromise) {
    pdfjsPromise = import(PDFJS_URL).then((pdfjs) => {
      pdfjs.GlobalWorkerOptions.workerSrc = PDF_WORKER_URL;
      return pdfjs;
    });
  }
  return pdfjsPromise;
}

function setStatus(viewer, message) {
  const status = viewer.querySelector('[data-pdf-status]');
  if (status) status.textContent = message;
}

function createViewerState(viewer, pdf) {
  const mode = viewer.dataset.pdfMode || 'thumbnail';
  const stage = viewer.querySelector('[data-pdf-stage]');
  const canvas = viewer.querySelector('[data-pdf-canvas]');
  const context = canvas?.getContext('2d', { alpha: false });
  if (!stage || !canvas || !context) throw new Error('The preview canvas is unavailable.');

  const state = { viewer, pdf, stage, canvas, context, page: 1, zoom: 1, renderTask: null, resizeTimer: null };
  const pageInput = viewer.querySelector('[data-pdf-page-input]');
  const pageTotal = viewer.querySelector('[data-pdf-page-total]');
  const previous = viewer.querySelector('[data-pdf-previous]');
  const next = viewer.querySelector('[data-pdf-next]');
  const zoomOut = viewer.querySelector('[data-pdf-zoom-out]');
  const zoomIn = viewer.querySelector('[data-pdf-zoom-in]');
  const fit = viewer.querySelector('[data-pdf-fit]');

  const render = async () => {
    if (state.renderTask) {
      state.renderTask.cancel();
      state.renderTask = null;
    }
    const page = await pdf.getPage(state.page);
    const base = page.getViewport({ scale: 1 });
    const padding = mode === 'reader' ? 32 : 12;
    const availableWidth = Math.max(120, stage.clientWidth - padding);
    const availableHeight = Math.max(160, stage.clientHeight - padding);
    const fitScale = Math.min(availableWidth / base.width, availableHeight / base.height);
    const viewport = page.getViewport({ scale: fitScale * state.zoom });
    const outputScale = Math.min(window.devicePixelRatio || 1, 3);

    canvas.width = Math.ceil(viewport.width * outputScale);
    canvas.height = Math.ceil(viewport.height * outputScale);
    canvas.style.width = `${viewport.width}px`;
    canvas.style.height = `${viewport.height}px`;
    canvas.setAttribute('aria-label', `${viewer.dataset.pdfTitle || 'PDF'} — page ${state.page} of ${pdf.numPages}`);
    stage.dataset.ready = 'true';
    setStatus(viewer, '');

    const renderTask = page.render({
      canvasContext: context,
      viewport,
      transform: outputScale === 1 ? null : [outputScale, 0, 0, outputScale, 0, 0],
      background: '#ffffff'
    });
    state.renderTask = renderTask;
    try {
      await renderTask.promise;
    } catch (error) {
      if (error?.name !== 'RenderingCancelledException') throw error;
    } finally {
      if (state.renderTask === renderTask) state.renderTask = null;
    }

    if (pageInput) pageInput.value = String(state.page);
    if (pageTotal) pageTotal.textContent = `of ${pdf.numPages}`;
    if (previous) previous.disabled = state.page <= 1;
    if (next) next.disabled = state.page >= pdf.numPages;
    if (zoomOut) zoomOut.disabled = state.zoom <= 0.75;
    if (zoomIn) zoomIn.disabled = state.zoom >= 2.5;
    if (fit) fit.disabled = state.zoom === 1;
  };

  const changePage = (page) => {
    const nextPage = Math.min(pdf.numPages, Math.max(1, Number(page) || 1));
    if (nextPage === state.page && stage.dataset.ready === 'true') return;
    state.page = nextPage;
    state.zoom = 1;
    render().catch((error) => showError(viewer, error));
  };

  if (mode === 'reader') {
    previous?.addEventListener('click', () => changePage(state.page - 1));
    next?.addEventListener('click', () => changePage(state.page + 1));
    pageInput?.addEventListener('change', () => changePage(pageInput.value));
    zoomOut?.addEventListener('click', () => {
      state.zoom = Math.max(0.75, Math.round((state.zoom - 0.25) * 100) / 100);
      render().catch((error) => showError(viewer, error));
    });
    zoomIn?.addEventListener('click', () => {
      state.zoom = Math.min(2.5, Math.round((state.zoom + 0.25) * 100) / 100);
      render().catch((error) => showError(viewer, error));
    });
    fit?.addEventListener('click', () => {
      state.zoom = 1;
      render().catch((error) => showError(viewer, error));
    });
    stage.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowLeft') changePage(state.page - 1);
      if (event.key === 'ArrowRight') changePage(state.page + 1);
    });
  }

  viewer.addEventListener('contextmenu', (event) => event.preventDefault());
  const resizeObserver = new ResizeObserver(() => {
    if (stage.dataset.ready !== 'true') return;
    window.clearTimeout(state.resizeTimer);
    state.resizeTimer = window.setTimeout(() => render().catch((error) => showError(viewer, error)), 120);
  });
  resizeObserver.observe(stage);
  state.render = render;
  viewer._ziloopPdfState = state;
  render().catch((error) => showError(viewer, error));
}

function showError(viewer, error) {
  console.error('ZILOOP PDF preview failed:', error);
  const stage = viewer.querySelector('[data-pdf-stage]');
  if (stage) stage.dataset.ready = 'false';
  setStatus(viewer, 'This preview could not be loaded. Please refresh and try again.');
}

async function initializeViewer(viewer) {
  if (viewer.dataset.pdfInitialized === 'true') return;
  viewer.dataset.pdfInitialized = 'true';
  setStatus(viewer, 'Loading preview…');
  try {
    const url = viewer.dataset.pdfUrl;
    let pdfPromise = pdfDocumentCache.get(url);
    if (!pdfPromise) {
      const { getDocument } = await loadPdfJs();
      const loadingTask = getDocument({
        url,
        rangeChunkSize: 65536,
        disableRange: false,
        disableStream: false,
        isEvalSupported: false,
        useSystemFonts: true
      });
      loadingTask.onProgress = ({ loaded, total }) => {
        if (total) setStatus(viewer, `Loading preview… ${Math.min(100, Math.round((loaded / total) * 100))}%`);
      };
      pdfPromise = loadingTask.promise;
      pdfDocumentCache.set(url, pdfPromise);
    }
    let pdf;
    try {
      pdf = await pdfPromise;
    } catch (error) {
      pdfDocumentCache.delete(url);
      throw error;
    }
    createViewerState(viewer, pdf);
  } catch (error) {
    viewer.dataset.pdfInitialized = 'false';
    showError(viewer, error);
  }
}

function observeDocuments() {
  const viewers = document.querySelectorAll('[data-pdf-document]');
  const lazyObserver = new IntersectionObserver((entries) => {
    for (const entry of entries) {
      if (!entry.isIntersecting) continue;
      lazyObserver.unobserve(entry.target);
      initializeViewer(entry.target);
    }
  }, { rootMargin: '400px 0px' });

  viewers.forEach((viewer) => {
    if (viewer.dataset.pdfMode === 'reader') {
      const details = viewer.closest('details');
      if (details?.open) initializeViewer(viewer);
      details?.addEventListener('toggle', () => {
        if (details.open) initializeViewer(viewer);
      });
    } else {
      lazyObserver.observe(viewer);
    }
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', observeDocuments, { once: true });
} else {
  observeDocuments();
}
